import {
  getFlowNode,
  getQuestionNodes,
  getStartNodeId,
  normalizeGuideFlowAxis,
} from "./guideFlowCore";

/**
 * Layout uses content-aware estimates: cards grow with long titles/options (flex width + many rows),
 * so fixed px strides caused horizontal band overlap and vertical stacking overlap.
 */
const MIN_NODE_W = 260;
const MIN_NODE_H = 155;
const VERT_GAP = 48;

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** Rough card width for overlap-safe horizontal packing (matches wide builder cards). */
export function estimateGuideNodeLayoutWidth(node) {
  if (!node || typeof node !== "object") return MIN_NODE_W;
  const type = node.type || "question";
  if (type === "effect") return 300;
  const label = String(node.data?.label || node.title || "");
  const opts = asArray(node.data?.options);
  let maxChars = label.length;
  opts.forEach((o) => {
    maxChars = Math.max(maxChars, String(o?.label || o?.value || "").length);
  });
  const capped = Math.min(maxChars, 95);
  return Math.min(620, Math.max(MIN_NODE_W, 236 + capped * 5.8));
}

/** Rough card height for vertical stacking (header + option rows in GuideFlowBuilder). */
export function estimateGuideNodeLayoutHeight(node) {
  if (!node || typeof node !== "object") return MIN_NODE_H;
  const type = node.type || "question";
  if (type === "effect") return 168;
  const opts = asArray(node.data?.options);
  const header = 124;
  const perOpt = 58;
  const base = header + Math.max(1, opts.length) * perOpt + 44;
  return Math.max(MIN_NODE_H, Math.min(1400, base));
}

/** Approximate card width for crowded detection (keep in sync with estimates above). */
const NODE_BOX_W = 320;

/** Spread under this (px) triggers auto-layout when using heuristic detection (optional callers). */
const TIGHT_CLUSTER_SPREAD = 160;

/** Horizontal padding between sibling columns inside one BFS depth band. */
const INTRA_DEPTH_COLUMN_GAP = 72;
/** Minimum horizontal padding between one depth band and the next. */
const DEPTH_BAND_GAP = 110;
/** Max nodes in one vertical stack before starting another column within the same depth. */
const MAX_NODES_PER_VERTICAL_STACK = 5;

const ORIGIN_X = 100;
const ORIGIN_Y = 100;

function outgoingTargets(flow, nodeId) {
  const node = getFlowNode(flow, nodeId);
  if (!node) return [];
  const out = [];
  if ((node.type || "question") === "effect") {
    const next = node.data?.next_node_id;
    if (next != null && String(next).trim() !== "") out.push(String(next));
    return out;
  }
  asArray(node.data?.options).forEach((opt) => {
    if (opt?.next_node_id != null && String(opt.next_node_id).trim() !== "") {
      out.push(String(opt.next_node_id));
    }
  });
  return [...new Set(out)];
}

/** Includes synthetic next question when options have no `next_node_id` (matches runtime linear fallback). */
function outgoingTargetsForLayout(flow, nodeId) {
  const fromGraph = outgoingTargets(flow, nodeId);
  if (fromGraph.length > 0) return fromGraph;

  const node = getFlowNode(flow, nodeId);
  if (!node || (node.type || "question") === "effect") return [];

  const questions = getQuestionNodes(flow).map((n) => String(n.id));
  const idx = questions.indexOf(String(nodeId));
  if (idx >= 0 && idx < questions.length - 1) return [questions[idx + 1]];

  return [];
}

/**
 * Depth-first visit order from `start_node_id`, then remaining ids — stabil vertical order within a layer.
 */
function computeVisitOrder(flow, idSet) {
  const order = [];
  const seen = new Set();

  let start = getStartNodeId(flow);
  if (!start || !idSet.has(String(start))) {
    start = flow.nodes?.[0]?.id;
  }

  function dfs(id) {
    const sid = String(id);
    if (!idSet.has(sid) || seen.has(sid)) return;
    seen.add(sid);
    order.push(sid);
    outgoingTargetsForLayout(flow, sid).forEach((tid) => dfs(tid));
  }

  dfs(start);

  [...idSet]
    .sort((a, b) => a.localeCompare(b))
    .forEach((id) => {
      if (!seen.has(id)) dfs(id);
    });

  return order;
}

/** True when importer should overwrite positions (missing in JSON or unreadable cluster). */
export function importedFlowNeedsAutoLayout(rawNodes, normalizedFlow) {
  const raw = asArray(rawNodes);
  const missingExplicit = raw.some((n) => !hasExplicitNodePosition(n));
  if (missingExplicit) return true;
  return shouldCompactClusterLayout(normalizedFlow);
}

function hasExplicitNodePosition(node) {
  if (!node || typeof node !== "object") return false;
  if (!Object.prototype.hasOwnProperty.call(node, "x") || !Object.prototype.hasOwnProperty.call(node, "y")) {
    return false;
  }
  if (node.x === null || node.x === undefined || node.y === null || node.y === undefined) return false;
  if (node.x === "" || node.y === "") return false;
  const x = normalizeGuideFlowAxis(node.x, NaN);
  const y = normalizeGuideFlowAxis(node.y, NaN);
  return Number.isFinite(x) && Number.isFinite(y);
}

/** Approximate card footprint for crowded detection (top-left spacing vs rendered bulk). */
const NODE_BOX_H = 340;

/** Any pair closer than this center-to-center is treated as overlapping layout. */
export function flowNodesVisuallyCrowded(flow) {
  const nodes = asArray(flow?.nodes);
  if (nodes.length < 2) return false;
  const minDist = Math.min(NODE_BOX_W, NODE_BOX_H) * 0.82;

  for (let i = 0; i < nodes.length; i++) {
    const ax = Number(nodes[i].x);
    const ay = Number(nodes[i].y);
    if (!Number.isFinite(ax) || !Number.isFinite(ay)) return true;
    for (let j = i + 1; j < nodes.length; j++) {
      const bx = Number(nodes[j].x);
      const by = Number(nodes[j].y);
      if (!Number.isFinite(bx) || !Number.isFinite(by)) return true;
      const dx = Math.abs(ax - bx);
      const dy = Math.abs(ay - by);
      if (dx < minDist && dy < minDist) return true;
    }
  }
  return false;
}

/** Normalized flow: detect overlapping / tiny bounding box. */
export function shouldCompactClusterLayout(flow) {
  const nodes = asArray(flow?.nodes);
  if (nodes.length <= 1) return false;

  if (flowNodesVisuallyCrowded(flow)) return true;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const n of nodes) {
    const x = Number(n.x);
    const y = Number(n.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return true;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  const spreadX = maxX - minX;
  const spreadY = maxY - minY;

  if (spreadX <= TIGHT_CLUSTER_SPREAD && spreadY <= TIGHT_CLUSTER_SPREAD) return true;

  const keys = nodes.map((n) => `${Number(n.x)},${Number(n.y)}`);
  if (new Set(keys).size === 1) return true;

  /** Many nodes on one horizontal line with little vertical spread — typical broken AI layout. */
  if (nodes.length >= 4 && spreadY <= 100 && spreadX < NODE_BOX_W * nodes.length * 0.45) return true;

  return false;
}

/**
 * Assign x/y from graph traversal (BFS depth = column, branch stacking = rows).
 * Keeps `data` untouched; shallow-clones nodes.
 */
export function autoLayoutGuideFlow(flow) {
  const nodes = asArray(flow?.nodes).map((n) => ({ ...n }));
  if (nodes.length === 0) {
    return { ...flow, nodes };
  }

  const workingFlow = { ...flow, nodes };
  const idSet = new Set(nodes.map((n) => String(n.id)));

  const layers = new Map();
  let start = getStartNodeId(workingFlow);
  if (!start || !idSet.has(String(start))) {
    start = nodes[0].id;
  }

  const queue = [[String(start), 0]];

  while (queue.length) {
    const [id, depth] = queue.shift();
    if (!idSet.has(id)) continue;
    if (layers.has(id)) continue;
    layers.set(id, depth);

    outgoingTargetsForLayout(workingFlow, id).forEach((tid) => {
      if (idSet.has(tid)) queue.push([tid, depth + 1]);
    });
  }

  let maxDepth = 0;
  layers.forEach((d) => {
    maxDepth = Math.max(maxDepth, d);
  });

  const orphanDepth = maxDepth + 1;
  nodes.forEach((n) => {
    const id = String(n.id);
    if (!layers.has(id)) layers.set(id, orphanDepth);
  });

  const visitOrder = computeVisitOrder(workingFlow, idSet);
  const rank = new Map(visitOrder.map((id, i) => [id, i]));

  const byLayer = new Map();
  nodes.forEach((n) => {
    const d = layers.get(String(n.id)) ?? orphanDepth;
    if (!byLayer.has(d)) byLayer.set(d, []);
    byLayer.get(d).push(n);
  });

  const depthKeys = [...byLayer.keys()].sort((a, b) => a - b);

  /** Advance X so wide cards / multi-column bands do not collide with the next depth. */
  let nextBandLeft = ORIGIN_X;
  depthKeys.forEach((depth) => {
    const row = byLayer.get(depth);
    row.sort((a, b) => {
      const ra = rank.get(String(a.id)) ?? 999999;
      const rb = rank.get(String(b.id)) ?? 999999;
      if (ra !== rb) return ra - rb;
      return String(a.id).localeCompare(String(b.id));
    });

    const bandLeft = nextBandLeft;
    let bandRight = bandLeft;

    const stacks = new Map();
    row.forEach((node, idx) => {
      const stackIndex = Math.floor(idx / MAX_NODES_PER_VERTICAL_STACK);
      if (!stacks.has(stackIndex)) stacks.set(stackIndex, []);
      stacks.get(stackIndex).push(node);
    });

    let xAt = bandLeft;
    const orderedStackKeys = [...stacks.keys()].sort((a, b) => a - b);
    orderedStackKeys.forEach((stackKey) => {
      const stackNodes = stacks.get(stackKey);
      let yAt = ORIGIN_Y;
      let colMaxW = MIN_NODE_W;
      stackNodes.forEach((node) => {
        const w = estimateGuideNodeLayoutWidth(node);
        const h = estimateGuideNodeLayoutHeight(node);
        colMaxW = Math.max(colMaxW, w);
        node.x = xAt;
        node.y = yAt;
        yAt += h + VERT_GAP;
      });
      bandRight = Math.max(bandRight, xAt + colMaxW);
      xAt += colMaxW + INTRA_DEPTH_COLUMN_GAP;
    });

    nextBandLeft = bandRight + DEPTH_BAND_GAP;
  });

  return {
    ...workingFlow,
    nodes,
    viewport: { x: 80, y: 40, zoom: 1 },
  };
}
